import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { Tendv2ContentItem, OverlayTemplateConfig, PlacedDesignElement, MAX_DESIGN_ELEMENTS_PER_OVERLAY } from '../../api/tendApi';
import { Logo } from '../../api/logoApi';
import LogoSelector, { LogoSelectorHandle } from '../brand/LogoSelector';
import ColorPickerPopover from './ColorPickerPopover';
import { useFontLibrary } from '../../contexts/FontLibraryContext';
import { resolveElementFontFamily } from '../../utils/fontResolution';
import AnchoredStripPopover from './overlay-controls/AnchoredStripPopover';
import TextWidthHandles from './overlay-controls/TextWidthHandles';
import { buildTextStyle, buildBorderStyle, TextCase, backdropBlurCss } from './overlay-controls/textStyleUtils';
import { OVERLAY_REFERENCE_WIDTH, OVERLAY_CONSTANTS, getScaledOverlayStyles } from '../../hooks/useOverlayScale';
import DesignElementsPicker from './design-elements/DesignElementsPicker';
import PlaceableDesignElement from './design-elements/PlaceableDesignElement';
import ElementFrame from './overlay-controls/ElementFrame';
import ElementToolbar, { TextToolbarValue } from './overlay-controls/ElementToolbar';
import { useOverlaySelection } from './overlay-controls/useOverlaySelection';
import { useElementMode } from './overlay-controls/useElementMode';
import { useArrowNudge } from './overlay-controls/useArrowNudge';
import SafeZoneLayer from './overlay-controls/SafeZoneLayer';
import CornerScaleHandle from './overlay-controls/CornerScaleHandle';
import { PLAIN_SAFE_ZONE, resolveSafeZone, SafeZoneInsets, strictestSafeZoneForBucket } from '../../utils/safeZones';
import { effectiveGap, readGaps, SetElementKind, stackBoxStyle, stackColumnStyle, stackKindsForRow, stackMargins } from '../../utils/overlayStack';
import { clampPlacement, placedElementCenterPx, deriveAnchoredOffsets } from './design-elements/designElementPosition';
import { splitDesignElementsByDepth } from '../../utils/designElementLayers';
import { useAuth } from '../../AuthContext';
import { BUCKET_CSS, BUCKET_DIMENSIONS, asAspectBucket, resolveChannelAspect } from '../../utils/channelAspect';
import { useBrandPalette } from '../../contexts/BrandPaletteContext';

interface OverlayPreviewProps {
  contentItem: Tendv2ContentItem;
  config: OverlayTemplateConfig;
  brandLogo: Logo | null;
  brandName: string;
  /** Brand the preview belongs to. Drives the design-elements picker's
   *  Brand-vs-Universal scope. Falls back to contentItem.brand_id if omitted. */
  brandId?: number | null;
  onConfigChange: (updates: Partial<OverlayTemplateConfig>) => void;
  // Optional overrides for text content
  titleOverride?: string;
  subheaderOverride?: string;
  bodyOverride?: string;
  categoryOverride?: string;
  // Size variant
  size?: 'small' | 'medium' | 'large';
  // Whether controls are enabled
  controlsEnabled?: boolean;
  /** Which safe zone the canvas shows: the content item's channel (+ placement), or the
      strictest zone of the canvas bucket for a channel-less template tile. */
  safeZoneScope?: 'channel' | 'bucket';
  safeZonePlacement?: string | null;
  /** Template-editor contexts: allow picking a specific logo into the config
      (config.logoUrl/logoKey/logoDisplayMode). Brand-scope templates only —
      a global template must not carry one brand's logo. */
  logoSelectable?: boolean;
  /** Display-mode fallback when config carries none (brand's current mode). */
  logoDisplayMode?: string;
  // Category styling from brand
  categoryBorderRadius?: number;
  categoryBgColor?: string;
  // Callback when category border radius changes (to persist to brand)
  onCategoryBorderRadiusChange?: (radius: number) => void;
  // Callback when category bg color changes (to persist to brand)
  onCategoryBgColorChange?: (color: string) => void;
  /** Template-preview contexts: solid fill shown when the content item has
      no media (or it fails to load) instead of the placeholder image.
      Omitted = legacy placeholder behavior, unchanged for other consumers. */
  fallbackBgColor?: string | null;
}

const OverlayPreview: React.FC<OverlayPreviewProps> = ({
  contentItem,
  config,
  brandLogo,
  brandName,
  brandId,
  onConfigChange,
  titleOverride,
  subheaderOverride,
  bodyOverride,
  categoryOverride,
  size = 'medium',
  controlsEnabled = true,
  safeZoneScope = 'channel',
  safeZonePlacement = null,
  logoSelectable = false,
  logoDisplayMode = 'icon_and_name',
  categoryBorderRadius: propCategoryBorderRadius = 50,
  categoryBgColor: propCategoryBgColor,
  onCategoryBorderRadiusChange,
  onCategoryBgColorChange,
  fallbackBgColor,
}) => {
  // Font Library manifest (null-object without provider → Inter fallback)
  const { manifest: fontManifest } = useFontLibrary();
  const { channelAspect, universalChannelAspect, universalSafeZones, setUniversalSafeZones } = useBrandPalette();

  // Template-carried logo: config keys win over the brand-default prop, so
  // a template's saved logo previews faithfully in the editor.
  const logoSelectorRef = useRef<LogoSelectorHandle>(null);
  const effectiveLogo = useMemo<Logo | null>(() => (
    config.logoUrl
      ? ({ url: config.logoUrl, logoKey: config.logoKey || 'icon_white', displayName: '' } as Logo)
      : brandLogo
  ), [config.logoUrl, config.logoKey, brandLogo]);
  const effectiveLogoDisplayMode = (config.logoDisplayMode as any) || logoDisplayMode;

  const [showControls, setShowControls] = useState(false);
  const [showGradientPanel, setShowGradientPanel] = useState(false);
  const [showBorderPanel, setShowBorderPanel] = useState(false);
  // Anchors for the portaled background/border strips (see AnchoredStripPopover)
  const gradientBtnRef = useRef<HTMLButtonElement>(null);
  const borderBtnRef = useRef<HTMLButtonElement>(null);
  const [showDesignElementsPicker, setShowDesignElementsPicker] = useState(false);
  // Where a BEHIND design element draws its selection chrome (above the set elements).
  const [designChromeHost, setDesignChromeHost] = useState<HTMLDivElement | null>(null);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.is_super_admin ?? false;
  // Template editor allows uploading to a brand collection if the user has
  // editor+ access to the template's brand. Prefer the explicit brandId prop
  // (set by TemplateManagementModal) and fall back to whatever's on the
  // synthetic contentItem.
  const previewBrandId = brandId ?? (contentItem as any).brand_id ?? null;
  const canEditPreviewBrand = useMemo(() => {
    if (isSuperAdmin) return true;
    if (!user?.brands || previewBrandId == null) return false;
    return user.brands.some((b) => String(b.id) === String(previewBrandId));
  }, [isSuperAdmin, user?.brands, previewBrandId]);
  // Container ref for proportional scaling
  const overlayContainerRef = useRef<HTMLDivElement | null>(null);

  // One selection for set elements and design elements alike: click selects,
  // click-away / Escape deselects, hover does nothing (useOverlaySelection).
  const selection = useOverlaySelection(overlayContainerRef, controlsEnabled);
  const { select: selectElement, clear: clearSelection, isSelected: isElementSelected } = selection;
  const selectedDesignElementId = selection.selected?.kind === 'design' ? selection.selected.id : null;
  // Element mode (Overlay controls hide on element selection, 2026-10-27):
  // a selected element closes the settings puck and its strips, and the
  // bottom-right panel stays gone until it is deselected.
  const elementMode = useElementMode(selection.selected, () => {
    setShowControls(false);
    setShowGradientPanel(false);
    setShowBorderPanel(false);
  });

  // Delete / Backspace removes the selected design element (skipped when
  // the user is typing into a text field).
  useEffect(() => {
    if (!selectedDesignElementId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const ae = document.activeElement as HTMLElement | null;
      const tag = ae?.tagName;
      const isEditable = !!ae && (
        tag === 'INPUT' || tag === 'TEXTAREA' || ae.isContentEditable
      );
      if (isEditable) return;
      e.preventDefault();
      const id = selectedDesignElementId;
      const next = (config.designElements || []).filter((x) => x.instanceId !== id);
      onConfigChange({ designElements: next });
      clearSelection();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [selectedDesignElementId, config.designElements, onConfigChange, clearSelection]);
  const [overlayScale, setOverlayScale] = useState(1);

  // Callback ref (not a mount-once effect) so the ResizeObserver re-attaches
  // if the container remounts — otherwise the scale freezes at a stale width
  // and the overlay drifts from the server render. offsetWidth so ancestor
  // transform animations can't skew the measurement.
  const overlayResizeObserverRef = useRef<ResizeObserver | null>(null);
  const setOverlayContainer = useCallback((node: HTMLDivElement | null) => {
    overlayContainerRef.current = node;
    overlayResizeObserverRef.current?.disconnect();
    overlayResizeObserverRef.current = null;
    if (!node) return;
    const updateScale = () => {
      const width = node.offsetWidth;
      if (width > 0) setOverlayScale(width / OVERLAY_REFERENCE_WIDTH);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    overlayResizeObserverRef.current = observer;
  }, []);

  // Extract config values with defaults
  const {
    showOverlayLogo = true,
    showOverlayTitle = true,
    showOverlaySubheader = false,
    showOverlayBody = false,
    showOverlayCategory = false,
    showOverlayGradient = true,
    logoPosition = 'bottom-center',
    titlePosition = 'bottom-center',
    subheaderPosition = 'bottom-center',
    bodyPosition = 'bottom-center',
    categoryPosition = 'top-left',
    titleFontSize = 16,
    subheaderFontSize = 12,
    bodyFontSize = 12,
    logoScale = 1.0,
    categoryScale = 1.0,
    gradientDirection = 'up',
    gradientColor = '#000000',
    gradientOpacity = 70,
    titleBgEnabled = false,
    titleBgColor = '#000000',
    titleBgOpacity = 80,
    titleTextColor = '#ffffff',
    subheaderBgEnabled = false,
    subheaderBgColor = '#000000',
    subheaderBgOpacity = 80,
    subheaderTextColor = '#ffffff',
    bodyBgEnabled = false,
    bodyBgColor = '#000000',
    bodyBgOpacity = 80,
    bodyTextColor = '#ffffff',
    borderEnabled = false,
    borderColor = '#ffffff',
    borderThickness = 4,
    categoryBgColor: configCategoryBgColor = '#2563eb',
    imageFitMode = 'fill',
    imageFitAnchor = 'center',
    imageFitWidth = 100,
    imageFillPositionX = 50,
    imageFillPositionY = 50,
    // Per-element extended styling (Title) — defaults bolded to match legacy semibold
    titleBold = true,
    titleItalic = false,
    titleUnderline = false,
    titleStrikethrough = false,
    titleShadow = true,
    titleTextCase = 'none',
    titleBorderEnabled = false,
    titleBorderColor = '#ffffff',
    titleBorderThickness = 0,
    titleBorderRadius = 8,
    titleWidth = 100,
    titleWidthOffset = 0,
    // Per-element extended styling (Subheader)
    subheaderBold = false,
    subheaderItalic = false,
    subheaderUnderline = false,
    subheaderStrikethrough = false,
    subheaderShadow = true,
    subheaderTextCase = 'none',
    subheaderBorderEnabled = false,
    subheaderBorderColor = '#ffffff',
    subheaderBorderThickness = 0,
    subheaderBorderRadius = 8,
    subheaderWidth = 100,
    subheaderWidthOffset = 0,
    // Per-element extended styling (Body)
    bodyBold = false,
    bodyItalic = false,
    bodyUnderline = false,
    bodyStrikethrough = false,
    bodyShadow = true,
    bodyTextCase = 'none',
    bodyBorderEnabled = false,
    bodyBorderColor = '#ffffff',
    bodyBorderThickness = 0,
    bodyBorderRadius = 8,
    bodyWidth = 100,
    bodyWidthOffset = 0,
    designElements = [],
  } = config;

  // Use prop categoryBgColor or config value
  const effectiveCategoryBgColor = propCategoryBgColor || configCategoryBgColor;
  const effectiveCategoryBorderRadius = propCategoryBorderRadius;

  // Content values
  const title = titleOverride ?? contentItem.source_title ?? contentItem.headline ?? '';
  const subheader = subheaderOverride ?? contentItem.source_subhead ?? '';
  // Body falls back through: explicit override → parent content's generated_body
  // → first carousel slide's generated_body → empty placeholder.
  const body = bodyOverride
    ?? contentItem.generated_body
    ?? contentItem.slides?.[0]?.generated_body
    ?? '';
  const rawCategory = categoryOverride ?? contentItem.source_category ?? '';
  // Show 'CATEGORY' placeholder if no category but showOverlayCategory is enabled
  const category = rawCategory || 'CATEGORY';
  const isCategoryPlaceholder = !rawCategory;

  // Check if URL is a video
  const isVideoUrl = (url: string) => /\.(mp4|mov|webm)$/i.test(url);
  const mediaUrl = contentItem.primary_image_url || '/placeholder-image.png';
  const isVideo = isVideoUrl(mediaUrl);

  // Template-preview fallback: solid brand color when there's no media (or
  // it fails to load) and a fallbackBgColor was provided
  const [mediaFailed, setMediaFailed] = useState(false);
  useEffect(() => {
    setMediaFailed(false);
  }, [contentItem.primary_image_url]);
  const useColorFill =
    fallbackBgColor != null && (!contentItem.primary_image_url || mediaFailed);
  const handleMediaError = () => setMediaFailed(true);

  // Canvas aspect: the content's stamped bucket, else brand/universal
  // resolution (matches the card + backend renderer; no 'auto' anymore)
  const previewBucket =
    asAspectBucket(contentItem.aspect_ratio) ??
    resolveChannelAspect(channelAspect, universalChannelAspect, contentItem.platform);
  const aspectRatio = BUCKET_CSS[previewBucket];

  // Size classes - match TendV2ContentCard image sizes
  const sizeClasses = {
    small: 'max-w-[200px]',
    medium: 'max-w-[300px]',
    large: '', // No max-width, fill container (used in template grid)
  };

  // Scaled styles for proportional rendering (matches backend overlay.html)
  const scaledStyles = useMemo(() =>
    getScaledOverlayStyles(overlayScale, titleFontSize, subheaderFontSize),
    [overlayScale, titleFontSize, subheaderFontSize]
  );

  // Helper to get alignment classes
  const getAlignClass = (zone: string) => {
    if (zone.endsWith('left')) return 'self-start text-left';
    if (zone.endsWith('right')) return 'self-end text-right';
    return 'self-center text-center';
  };

  const getJustifyClass = (zone: string) => {
    if (zone.endsWith('left')) return 'justify-start';
    if (zone.endsWith('right')) return 'justify-end';
    return 'justify-center';
  };

  // Radius slider per-tick handler — intentionally inert: the slider value is
  // the propCategoryBorderRadius passthrough (no local config state), so there
  // is nothing to preview locally. Persistence happens on release via the
  // commit handler below, never per tick.
  const handleCategoryBorderRadiusChange = (_radius: number) => {
  };

  // Committed radius → brand-level persistence, when the parent wires the prop
  const handleCategoryBorderRadiusCommit = (radius: number) => {
    if (onCategoryBorderRadiusChange) {
      onCategoryBorderRadiusChange(radius);
    }
  };

  // Handle category bg color change — live preview per tick (local config only)
  const handleCategoryBgColorChange = (color: string) => {
    onConfigChange({ categoryBgColor: color });
  };

  // Committed color → brand-level persistence, when the parent wires the prop
  // (kept off the per-tick path so a drag can never PATCH per tick)
  const handleCategoryBgColorCommit = (color: string) => {
    if (onCategoryBgColorChange) {
      onCategoryBgColorChange(color);
    }
  };

  // Reusable inline toolbar for the title/subheader/body text overlay elements
  // (parallel to the same helper in TendV2ContentCard, but bound to onConfigChange).
  // Order: [hide] [text color] [font size + ±] [Td] [element design] [vertical ±] [anchor].
  // popoverPosition mirrors the toolbar's own placement so submenus expand
  // away from the text element.
  // Props for the shared ElementToolbar (text kinds): reads one element's
  // config keys and writes a partial back through onConfigChange.
  const textToolbarProps = (kind: 'title' | 'subheader' | 'body') => {
    const c = config as unknown as Record<string, unknown>;
    const base = kind === 'title'
      ? {
          fontSize: titleFontSize, textColor: titleTextColor,
          bgEnabled: titleBgEnabled, bgColor: titleBgColor, bgOpacity: titleBgOpacity,
          position: titlePosition,
          bold: titleBold, italic: titleItalic, underline: titleUnderline,
          strikethrough: titleStrikethrough, shadow: titleShadow, textCase: titleTextCase as TextCase,
          borderEnabled: titleBorderEnabled, borderColor: titleBorderColor,
          borderThickness: titleBorderThickness, borderRadius: titleBorderRadius,
          gap: effectiveGapFor('title'),
        }
      : kind === 'subheader'
        ? {
            fontSize: subheaderFontSize, textColor: subheaderTextColor,
            bgEnabled: subheaderBgEnabled, bgColor: subheaderBgColor, bgOpacity: subheaderBgOpacity,
            position: subheaderPosition,
            bold: subheaderBold, italic: subheaderItalic, underline: subheaderUnderline,
            strikethrough: subheaderStrikethrough, shadow: subheaderShadow, textCase: subheaderTextCase as TextCase,
            borderEnabled: subheaderBorderEnabled, borderColor: subheaderBorderColor,
            borderThickness: subheaderBorderThickness, borderRadius: subheaderBorderRadius,
            gap: effectiveGapFor('subheader'),
          }
        : {
            fontSize: bodyFontSize, textColor: bodyTextColor,
            bgEnabled: bodyBgEnabled, bgColor: bodyBgColor, bgOpacity: bodyBgOpacity,
            position: bodyPosition,
            bold: bodyBold, italic: bodyItalic, underline: bodyUnderline,
            strikethrough: bodyStrikethrough, shadow: bodyShadow, textCase: bodyTextCase as TextCase,
            borderEnabled: bodyBorderEnabled, borderColor: bodyBorderColor,
            borderThickness: bodyBorderThickness, borderRadius: bodyBorderRadius,
            gap: effectiveGapFor('body'),
          };
    const value: TextToolbarValue = {
      ...base,
      fontFamily: (c[`${kind}FontFamily`] as string | undefined) ?? null,
      letterSpacing: (c[`${kind}LetterSpacing`] as number | undefined) ?? 0,
      lineHeight: (c[`${kind}LineHeight`] as number | undefined) ?? null,
    };
    const keyFor: Record<keyof TextToolbarValue, string> = {
      textColor: `${kind}TextColor`, fontFamily: `${kind}FontFamily`, fontSize: `${kind}FontSize`,
      bold: `${kind}Bold`, italic: `${kind}Italic`, underline: `${kind}Underline`,
      strikethrough: `${kind}Strikethrough`, shadow: `${kind}Shadow`, textCase: `${kind}TextCase`,
      letterSpacing: `${kind}LetterSpacing`, lineHeight: `${kind}LineHeight`,
      bgEnabled: `${kind}BgEnabled`, bgColor: `${kind}BgColor`, bgOpacity: `${kind}BgOpacity`,
      borderEnabled: `${kind}BorderEnabled`, borderColor: `${kind}BorderColor`,
      borderThickness: `${kind}BorderThickness`, borderRadius: `${kind}BorderRadius`,
      gap: `${kind}Gap`, position: `${kind}Position`,
    };
    const onChange = (patch: Partial<TextToolbarValue>) => {
      const out: Record<string, unknown> = {};
      (Object.keys(patch) as (keyof TextToolbarValue)[]).forEach((k) => {
        let v: unknown = patch[k];
        // Sparse keys: unset reverts to "inherit" so unchanged configs keep
        // inheriting (fontFamily precedent).
        if (k === 'fontFamily' || k === 'lineHeight') v = v ?? undefined;
        if (k === 'letterSpacing') v = v || undefined;
        if (k === 'gap') v = Math.max(0, Math.round(Number(v) || 0));
        out[keyFor[k]] = v;
      });
      onConfigChange(out as Partial<OverlayTemplateConfig>);
    };
    return { value, onChange };
  };

  // ----- Safe zone -----
  const safeBucket = previewBucket;
  const safeZone: SafeZoneInsets & { bucket: string } =
    safeZoneScope === 'bucket' || !contentItem.platform
      ? strictestSafeZoneForBucket(universalSafeZones, universalChannelAspect, safeBucket)
      : resolveSafeZone(universalSafeZones, contentItem.platform, safeZonePlacement);
  const safeZoneChannel = safeZoneScope === 'bucket' || !contentItem.platform ? null : contentItem.platform;
  const safeZoneLabel = safeZoneChannel
    ? `${safeZoneChannel}${safeZonePlacement ? ' · ' + safeZonePlacement : ''}`
    : `${safeBucket} templates`;
  // The box the rows anchor in. A canvas with a channel anchors to that
  // channel's zone; a channel-less template tile keeps the plain inset (the
  // strictest-zone bands it can show are information, not layout — see the
  // "Templates × channel, placement and safe zone" ticket).
  const anchorZone: SafeZoneInsets = safeZoneChannel ? safeZone : PLAIN_SAFE_ZONE;
  // The stack: what is visible in which row, and each element's gap below it.
  const stackInput = {
    positions: { logo: logoPosition, title: titlePosition, subheader: subheaderPosition, body: bodyPosition, category: categoryPosition },
    visible: {
      logo: showOverlayLogo && !!(effectiveLogo || brandName),
      title: showOverlayTitle && !!title,
      subheader: showOverlaySubheader && !!subheader,
      body: showOverlayBody,
      category: showOverlayCategory && !!category,
    },
  };
  const stackGaps = readGaps(config as unknown as Record<string, unknown>);
  const effectiveGapFor = (kind: SetElementKind) => effectiveGap(kind, stackInput, stackGaps);
  // Per editor and not remembered: on for this canvas only, off again once the
  // canvas loses selection (settings puck closed and no element selected).
  const [showSafeZones, setShowSafeZones] = useState(false);
  const toggleSafeZones = () => setShowSafeZones((v) => !v);
  useEffect(() => {
    if (!showControls && !selection.selected) setShowSafeZones(false);
  }, [showControls, selection.selected]);

  // ----- Arrow keys: design elements only (1 px, Shift 10 px). Set elements keep
  // the stack + vertical-offset model; their movement is being rethought. -----
  const nudge = useCallback((sel: { kind: string; id?: string }, n: { dx: number; dy: number }) => {
    if (sel.kind !== 'design') return;
    const c = overlayContainerRef.current?.getBoundingClientRect();
    const el = (config.designElements || []).find((d) => d.instanceId === sel.id);
    if (!c || !el) return;
    const { centerX, centerY } = placedElementCenterPx(el, c.width, c.height);
    const next = clampPlacement({ ...el, ...deriveAnchoredOffsets(centerX + n.dx, centerY + n.dy, c.width, c.height) }, c.width, c.height);
    onConfigChange({ designElements: (config.designElements || []).map((d) => (d.instanceId === el.instanceId ? next : d)) });
  }, [config, onConfigChange]);
  useArrowNudge({ enabled: controlsEnabled, selected: selection.selected, rootRef: overlayContainerRef, onNudge: nudge });

  // Render overlay elements for a row
  const renderRow = (row: 'top' | 'middle' | 'bottom') => {
    const kinds = stackKindsForRow(row, stackInput);
    const items = kinds.map((type) => ({ type, zone: stackInput.positions[type] }));
    const margins = stackMargins(kinds, stackGaps, overlayScale);

    // Row padding style based on position (scaled)
    const getRowStyle = (): React.CSSProperties => {
      return { ...stackBoxStyle(row), pointerEvents: 'none' };
    };

    return (
      <div key={row} data-overlay-row={row} style={getRowStyle()}>
        {/* Rows take no pointer events of their own: each ElementFrame does, so blank
            row space falls through to a design element behind the text. */}
        <div style={{ pointerEvents: 'none', width: '100%', display: 'flex', flexDirection: 'column' }}>
          {items.map(({ type, zone }, itemIndex) => {
            const itemMargin = margins[itemIndex];
            // LOGO
            if (type === 'logo' && (effectiveLogo || brandName)) {
              return (
                <div
                  key="logo"
                  className={`w-full ${getAlignClass(zone)} relative flex ${getJustifyClass(zone)}`}
                  style={{ marginBottom: itemMargin }}
                >
                  <ElementFrame
                    elementKey="logo"
                    selected={isElementSelected('logo')}
                    editable={controlsEnabled}
                    onSelect={() => selectElement({ kind: 'logo' })}
                    onDismiss={() => { onConfigChange({ showOverlayLogo: false }); clearSelection(); }}
                    dismissTitle="Hide logo"
                    boundsRef={overlayContainerRef}
                    handles={(
                      <CornerScaleHandle
                        scale={logoScale}
                        onChange={(s) => onConfigChange({ logoScale: s })}
                        boundsRef={overlayContainerRef}
                        insets={anchorZone}
                        title="Drag to resize the logo"
                      />
                    )}
                    style={{ display: 'inline-flex' }}
                    toolbar={(placement) => (
                      <ElementToolbar
                        kind="logo"
                        placement={placement}
                        position={logoPosition}
                        onPositionChange={(pos) => onConfigChange({ logoPosition: pos })}
                        gap={effectiveGapFor('logo')}
                        onGapChange={(n) => onConfigChange({ logoGap: n })}
                        onChangeLogo={logoSelectable ? () => logoSelectorRef.current?.open() : undefined}
                        badge={logoSelectable ? (
                          <span className="text-[9px] uppercase tracking-wide text-white/50 pl-0.5 select-none" title={(config.logoUrl || config.logoDisplayMode) ? 'This template carries its own logo choice' : 'Following the brand default — pick a logo to pin one to this template'}>
                            {(config.logoUrl || config.logoDisplayMode) ? 'set' : 'default'}
                          </span>
                        ) : undefined}
                      />
                    )}
                  >
                  {/* Logo with zoom scaling — mirrors LogoSelector's trigger
                      rules so the template previews the logo exactly as the
                      content card will render it: explicit template logo →
                      image; custom brand variant → image; icon_white/none →
                      display-mode fallback (icon and/or brand name). */}
                  <div style={{ zoom: logoScale, position: 'relative' }}>
                    {(() => {
                      const templateModeOnly = !config.logoUrl && !!config.logoDisplayMode;
                      const imageLogo = config.logoUrl
                        ? effectiveLogo
                        : (!templateModeOnly && brandLogo && brandLogo.logoKey !== 'icon_white') ? brandLogo : null;
                      const logoHpx = parseFloat(String(scaledStyles.logoHeight)) || 24;
                      if (imageLogo) {
                        return (
                          <div style={{ display: 'flex', alignItems: 'center', gap: logoHpx * 0.25 }}>
                            <img
                              src={imageLogo.url}
                              alt={brandName}
                              style={{ height: scaledStyles.logoHeight, width: 'auto', objectFit: 'contain', maxWidth: scaledStyles.logoWideMax }}
                            />
                            {imageLogo.logoKey === 'icon_white' && (
                              <span style={{ fontSize: logoHpx * 0.56, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>{brandName}</span>
                            )}
                          </div>
                        );
                      }
                      const mode = effectiveLogoDisplayMode;
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: logoHpx * 0.25 }}>
                          {(mode === 'icon_only' || mode === 'icon_and_name') && (
                            brandLogo ? (
                              <img
                                src={brandLogo.url}
                                alt={brandName}
                                style={{ height: scaledStyles.logoHeight, width: 'auto', objectFit: 'contain', borderRadius: logoHpx * 0.3 }}
                              />
                            ) : (
                              <div style={{ height: logoHpx, width: logoHpx, background: '#7c3aed', borderRadius: logoHpx * 0.25, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: logoHpx * 0.5 }}>
                                {(brandName || '?').charAt(0)}
                              </div>
                            )
                          )}
                          {(mode === 'name_only' || mode === 'icon_and_name') && (
                            <span style={{ fontSize: logoHpx * 0.56, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap' }}>{brandName}</span>
                          )}
                        </div>
                      );
                    })()}
                    {/* Invisible LogoSelector over the logo box: gives the
                        portal dropdown a real anchor rect; opened only via
                        the toolbar button (ref.open) */}
                    {logoSelectable && controlsEnabled && brandId != null && (
                      <div className="absolute inset-0 opacity-0 pointer-events-none" aria-hidden>
                        <LogoSelector
                          ref={logoSelectorRef}
                          brandId={brandId}
                          brandName={brandName}
                          defaultLogo={effectiveLogo}
                          logoDisplayMode={effectiveLogoDisplayMode}
                          canEdit={true}
                          openOnClick={false}
                          onChange={(logo, displayMode) => onConfigChange({
                            logoUrl: logo?.url,
                            logoKey: logo?.logoKey,
                            logoDisplayMode: displayMode,
                          })}
                          skipBrandUpdate={true}
                        />
                      </div>
                    )}
                  </div>
                  </ElementFrame>
                </div>
              );
            }

            // TITLE
            if (type === 'title' && title) {
              const titleEmphasis = {
                ...buildTextStyle({
                  bold: titleBold, italic: titleItalic, underline: titleUnderline,
                  strikethrough: titleStrikethrough, shadow: titleShadow, shadowVariant: 'title',
                  textCase: titleTextCase as TextCase,
                  letterSpacing: (config as any).titleLetterSpacing ?? 0,
                  lineHeight: (config as any).titleLineHeight ?? null,
                }, overlayScale),
                fontFamily: resolveElementFontFamily(config, 'title', fontManifest),
              };
              const titleBox = buildBorderStyle({
                borderEnabled: titleBorderEnabled,
                borderColor: titleBorderColor,
                borderThickness: titleBorderThickness,
                borderRadius: titleBorderRadius,
              }, overlayScale);
              const titleToolbar = textToolbarProps('title');
              const titleAlign: 'left' | 'center' | 'right' = zone.endsWith('left') ? 'left' : zone.endsWith('right') ? 'right' : 'center';
              return (
                <ElementFrame
                  key="title"
                  elementKey="title"
                  selected={isElementSelected('title')}
                  editable={controlsEnabled}
                  onSelect={() => selectElement({ kind: 'title' })}
                  onDismiss={() => { onConfigChange({ showOverlayTitle: false }); clearSelection(); }}
                  dismissTitle="Hide title"
                  boundsRef={overlayContainerRef}
                  className="relative"
                  style={{
                    width: `${titleWidth}%`,
                    alignSelf: titleAlign === 'left' ? 'flex-start' : titleAlign === 'right' ? 'flex-end' : 'center',
                    textAlign: titleAlign,
                    marginLeft: titleAlign === 'left' ? `${titleWidthOffset}%` : undefined,
                    marginRight: titleAlign === 'right' ? `${titleWidthOffset}%` : undefined,
                    marginBottom: itemMargin,
                  }}
                  handles={(
                    <TextWidthHandles
                      widthPct={titleWidth}
                      widthOffsetPct={titleWidthOffset}
                      anchor={titleAlign}
                      onChange={({ width, widthOffset }) => onConfigChange({ titleWidth: width, titleWidthOffset: widthOffset })}
                      visible
                    />
                  )}
                  toolbar={(placement) => (
                    <ElementToolbar
                      kind="title"
                      placement={placement}
                      value={titleToolbar.value}
                      onChange={titleToolbar.onChange}
                    />
                  )}
                >
                  <div
                    className="leading-tight"
                    style={{
                      fontSize: `${scaledStyles.titleFont}px`,
                      color: titleTextColor,
                      backgroundColor: titleBgEnabled
                        ? `${titleBgColor}${Math.round(titleBgOpacity * 2.55).toString(16).padStart(2, '0')}`
                        : 'transparent',
                      borderRadius: titleBgEnabled ? scaledStyles.titleBgRadius : undefined,
                      padding: `${scaledStyles.textPadding}px`,
                      whiteSpace: 'pre-wrap',
                      ...titleEmphasis,
                      ...titleBox,
                    }}
                    dangerouslySetInnerHTML={{ __html: title }}
                  />
                </ElementFrame>
              );
            }

            // SUBHEADER
            if (type === 'subheader' && subheader) {
              const subheaderEmphasis = {
                ...buildTextStyle({
                bold: subheaderBold, italic: subheaderItalic, underline: subheaderUnderline,
                strikethrough: subheaderStrikethrough, shadow: subheaderShadow, textCase: subheaderTextCase as TextCase,
                letterSpacing: (config as any).subheaderLetterSpacing ?? 0,
                lineHeight: (config as any).subheaderLineHeight ?? null,
                }, overlayScale),
                fontFamily: resolveElementFontFamily(config, 'subheader', fontManifest),
              };
              const subheaderBox = buildBorderStyle({
                borderEnabled: subheaderBorderEnabled,
                borderColor: subheaderBorderColor,
                borderThickness: subheaderBorderThickness,
                borderRadius: subheaderBorderRadius,
              }, overlayScale);
              const subheaderToolbar = textToolbarProps('subheader');
              const subheaderAlign: 'left' | 'center' | 'right' = zone.endsWith('left') ? 'left' : zone.endsWith('right') ? 'right' : 'center';
              return (
                <ElementFrame
                  key="subheader"
                  elementKey="subheader"
                  selected={isElementSelected('subheader')}
                  editable={controlsEnabled}
                  onSelect={() => selectElement({ kind: 'subheader' })}
                  onDismiss={() => { onConfigChange({ showOverlaySubheader: false }); clearSelection(); }}
                  dismissTitle="Hide subheader"
                  boundsRef={overlayContainerRef}
                  className="relative"
                  style={{
                    width: `${subheaderWidth}%`,
                    alignSelf: subheaderAlign === 'left' ? 'flex-start' : subheaderAlign === 'right' ? 'flex-end' : 'center',
                    textAlign: subheaderAlign,
                    marginLeft: subheaderAlign === 'left' ? `${subheaderWidthOffset}%` : undefined,
                    marginRight: subheaderAlign === 'right' ? `${subheaderWidthOffset}%` : undefined,
                    marginBottom: itemMargin,
                  }}
                  handles={(
                    <TextWidthHandles
                      widthPct={subheaderWidth}
                      widthOffsetPct={subheaderWidthOffset}
                      anchor={subheaderAlign}
                      onChange={({ width, widthOffset }) => onConfigChange({ subheaderWidth: width, subheaderWidthOffset: widthOffset })}
                      visible
                    />
                  )}
                  toolbar={(placement) => (
                    <ElementToolbar
                      kind="subheader"
                      placement={placement}
                      value={subheaderToolbar.value}
                      onChange={subheaderToolbar.onChange}
                    />
                  )}
                >
                  <div
                    className="leading-snug"
                    style={{
                      fontSize: `${scaledStyles.subheaderFont}px`,
                      color: subheaderTextColor,
                      backgroundColor: subheaderBgEnabled
                        ? `${subheaderBgColor}${Math.round(subheaderBgOpacity * 2.55).toString(16).padStart(2, '0')}`
                        : 'transparent',
                      borderRadius: subheaderBgEnabled ? scaledStyles.subheaderBgRadius : undefined,
                      padding: `${scaledStyles.textPadding}px`,
                      whiteSpace: 'pre-wrap',
                      ...subheaderEmphasis,
                      ...subheaderBox,
                    }}
                    dangerouslySetInnerHTML={{ __html: subheader }}
                  />
                </ElementFrame>
              );
            }

            // BODY — third overlay text element. Renders if showOverlayBody is on.
            // Uses a placeholder string when no body content is available so that
            // template thumbnails still show what the toggled-on body element looks like.
            if (type === 'body') {
              const bodyDisplay = body || 'Body text for this slide';
              const isBodyPlaceholder = !body;
              const bodyEmphasis = {
                ...buildTextStyle({
                bold: bodyBold, italic: bodyItalic, underline: bodyUnderline,
                strikethrough: bodyStrikethrough, shadow: bodyShadow, textCase: bodyTextCase as TextCase,
                letterSpacing: (config as any).bodyLetterSpacing ?? 0,
                lineHeight: (config as any).bodyLineHeight ?? null,
                }, overlayScale),
                fontFamily: resolveElementFontFamily(config, 'body', fontManifest),
              };
              const bodyBox = buildBorderStyle({
                borderEnabled: bodyBorderEnabled,
                borderColor: bodyBorderColor,
                borderThickness: bodyBorderThickness,
                borderRadius: bodyBorderRadius,
              }, overlayScale);
              const bodyToolbar = textToolbarProps('body');
              const bodyAlign: 'left' | 'center' | 'right' = zone.endsWith('left') ? 'left' : zone.endsWith('right') ? 'right' : 'center';
              return (
                <ElementFrame
                  key="body"
                  elementKey="body"
                  selected={isElementSelected('body')}
                  editable={controlsEnabled}
                  onSelect={() => selectElement({ kind: 'body' })}
                  onDismiss={() => { onConfigChange({ showOverlayBody: false }); clearSelection(); }}
                  dismissTitle="Hide body"
                  boundsRef={overlayContainerRef}
                  className="block relative"
                  style={{
                    width: `${bodyWidth}%`,
                    alignSelf: bodyAlign === 'left' ? 'flex-start' : bodyAlign === 'right' ? 'flex-end' : 'center',
                    textAlign: bodyAlign,
                    marginLeft: bodyAlign === 'left' ? `${bodyWidthOffset}%` : undefined,
                    marginRight: bodyAlign === 'right' ? `${bodyWidthOffset}%` : undefined,
                    marginBottom: itemMargin,
                    flexShrink: 0,
                  }}
                  handles={(
                    <TextWidthHandles
                      widthPct={bodyWidth}
                      widthOffsetPct={bodyWidthOffset}
                      anchor={bodyAlign}
                      onChange={({ width, widthOffset }) => onConfigChange({ bodyWidth: width, bodyWidthOffset: widthOffset })}
                      visible
                    />
                  )}
                  toolbar={(placement) => (
                    <ElementToolbar
                      kind="body"
                      placement={placement}
                      value={bodyToolbar.value}
                      onChange={bodyToolbar.onChange}
                    />
                  )}
                >
                  <div
                    className={`leading-snug ${isBodyPlaceholder ? 'italic opacity-50' : ''}`}
                    style={{
                      fontSize: `${bodyFontSize * overlayScale}px`,
                      color: bodyTextColor,
                      backgroundColor: bodyBgEnabled
                        ? `${bodyBgColor}${Math.round(bodyBgOpacity * 2.55).toString(16).padStart(2, '0')}`
                        : 'transparent',
                      borderRadius: bodyBgEnabled ? scaledStyles.subheaderBgRadius : undefined,
                      padding: `${scaledStyles.textPadding}px`,
                      whiteSpace: 'pre-wrap',
                      ...bodyEmphasis,
                      ...bodyBox,
                    }}
                    dangerouslySetInnerHTML={{ __html: isBodyPlaceholder ? bodyDisplay : bodyDisplay }}
                  />
                </ElementFrame>
              );
            }

            // CATEGORY
            if (type === 'category' && category) {
              // category_border_radius: 0=square, 100=pill. Scale by 0.1 for px, then by overlayScale
              const dynamicCategoryRadius = Math.round((effectiveCategoryBorderRadius / 10) * overlayScale);
              return (
                <div
                  key="category"
                  className={`w-full ${getAlignClass(zone)} relative flex ${getJustifyClass(zone)}`}
                  style={{ marginBottom: itemMargin }}
                >
                  <ElementFrame
                    elementKey="category"
                    selected={isElementSelected('category')}
                    editable={controlsEnabled}
                    onSelect={() => selectElement({ kind: 'category' })}
                    onDismiss={() => { onConfigChange({ showOverlayCategory: false }); clearSelection(); }}
                    dismissTitle="Hide category"
                    outlineRadius={dynamicCategoryRadius}
                    outlineOffset={1}
                    boundsRef={overlayContainerRef}
                    handles={(
                      <CornerScaleHandle
                        scale={categoryScale}
                        onChange={(s) => onConfigChange({ categoryScale: s })}
                        boundsRef={overlayContainerRef}
                        insets={anchorZone}
                        title="Drag to resize the category"
                      />
                    )}
                    style={{ display: 'inline-flex' }}
                    toolbar={(placement) => (
                      <ElementToolbar
                        kind="category"
                        placement={placement}
                        borderRadius={effectiveCategoryBorderRadius}
                        bgColor={effectiveCategoryBgColor}
                        fontFamily={config.categoryFontFamily ?? null}
                        position={categoryPosition}
                        onBorderRadiusPreview={handleCategoryBorderRadiusChange}
                        onBorderRadiusCommit={handleCategoryBorderRadiusCommit}
                        onBgColorPreview={handleCategoryBgColorChange}
                        onBgColorCommit={handleCategoryBgColorCommit}
                        onFontFamilyChange={(slug) => onConfigChange({ categoryFontFamily: slug ?? undefined })}
                        onPositionChange={(pos) => onConfigChange({ categoryPosition: pos })}
                        gap={effectiveGapFor('category')}
                        onGapChange={(n) => onConfigChange({ categoryGap: n })}
                      />
                    )}
                  >
                  <div style={{ zoom: categoryScale }}>
                  <span
                    className={`text-white font-medium uppercase tracking-wide
                      ${isCategoryPlaceholder ? 'italic opacity-50' : ''}`}
                    style={{
                      backgroundColor: effectiveCategoryBgColor,
                      borderRadius: `${dynamicCategoryRadius}px`,
                      fontSize: `${scaledStyles.categoryFont}px`,
                      padding: `${scaledStyles.categoryPadV}px ${scaledStyles.categoryPadH}px`,
                      fontFamily: resolveElementFontFamily(config, 'category', fontManifest),
                    }}
                  >
                    {category}
                  </span>
                  </div>
                  </ElementFrame>
                </div>
              );
            }

            return null;
          })}
        </div>
      </div>
    );
  };

  // Design elements: two layers by depth (utils/designElementLayers.ts). A
  // behind element draws its selection chrome in designChromeHost.
  const designLayers = splitDesignElementsByDepth(designElements);
  const renderDesignElement = (p: PlacedDesignElement) => (
    <PlaceableDesignElement
      key={p.instanceId}
      placement={p}
      containerRef={overlayContainerRef}
      chromeHost={designChromeHost}
      isSelected={selectedDesignElementId === p.instanceId}
      editable={controlsEnabled !== false}
      onSelect={() => selectElement({ kind: 'design', id: p.instanceId })}
      onChange={(next) => {
        const arr = (designElements || []).map((x) => (x.instanceId === p.instanceId ? next : x));
        onConfigChange({ designElements: arr });
      }}
      onDelete={() => {
        const arr = (designElements || []).filter((x) => x.instanceId !== p.instanceId);
        onConfigChange({ designElements: arr });
        if (selectedDesignElementId === p.instanceId) clearSelection();
      }}
    />
  );

  return (
    <div className={`relative ${sizeClasses[size]} w-full group`}>
      {/* Media container with aspect ratio - ref for proportional scaling */}
      <div
        ref={setOverlayContainer}
        className="relative w-full overflow-hidden rounded-lg"
        style={{ aspectRatio }}
      >
        {useColorFill ? (
          /* Template-preview fallback: solid brand color instead of media */
          <div className="absolute inset-0" style={{ background: fallbackBgColor as string }} />
        ) : imageFitMode === 'fit' ? (
          <>
            {/* Blurred background */}
            {isVideo ? (
              <video src={mediaUrl} className="absolute inset-0 w-full h-full object-cover" style={{ filter: backdropBlurCss(overlayScale), transform: 'scale(1.1)' }} muted loop playsInline autoPlay onError={handleMediaError} />
            ) : (
              <img src={mediaUrl} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: backdropBlurCss(overlayScale), transform: 'scale(1.1)' }} onError={handleMediaError} />
            )}
            {/* Contained foreground */}
            {isVideo ? (
              <video src={mediaUrl} className="absolute z-[1]" style={{ width: `${imageFitWidth}%`, height: '100%', objectFit: 'contain', objectPosition: `center ${imageFitAnchor}`, left: '50%', transform: 'translateX(-50%)', ...(imageFitWidth < 100 && imageFitAnchor === 'top' ? { paddingTop: 20 * overlayScale } : imageFitWidth < 100 && imageFitAnchor === 'bottom' ? { paddingBottom: 20 * overlayScale } : {}) }} muted loop playsInline autoPlay onError={handleMediaError} />
            ) : (
              <img src={mediaUrl} alt={title || 'Content preview'} className="absolute z-[1]" style={{ width: `${imageFitWidth}%`, height: '100%', objectFit: 'contain', objectPosition: `center ${imageFitAnchor}`, left: '50%', transform: 'translateX(-50%)', ...(imageFitWidth < 100 && imageFitAnchor === 'top' ? { paddingTop: 20 * overlayScale } : imageFitWidth < 100 && imageFitAnchor === 'bottom' ? { paddingBottom: 20 * overlayScale } : {}) }} onError={handleMediaError} />
            )}
          </>
        ) : isVideo ? (
          <video
            src={mediaUrl}
            className="w-full h-full object-cover"
            style={{ objectPosition: `${imageFillPositionX}% ${imageFillPositionY}%` }}
            muted
            loop
            playsInline
            autoPlay
            onError={handleMediaError}
          />
        ) : (
          <img
            src={mediaUrl}
            alt={title || 'Content preview'}
            className="w-full h-full object-cover"
            style={{ objectPosition: `${imageFillPositionX}% ${imageFillPositionY}%` }}
            onError={handleMediaError}
          />
        )}

        {/* Border layer */}
        {borderEnabled && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              border: `${borderThickness}px solid ${borderColor}`,
              borderRadius: 'inherit',
            }}
          />
        )}

        {/* Gradient overlay - matching TendV2ContentCard gradient style */}
        {showOverlayGradient && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                gradientDirection === 'full'
                  ? `${gradientColor}${Math.round(gradientOpacity * 2.55).toString(16).padStart(2, '0')}`
                  : gradientDirection === 'down'
                  ? `linear-gradient(to bottom, ${gradientColor}${Math.round(gradientOpacity * 2.55).toString(16).padStart(2, '0')} 0%, ${gradientColor}${Math.round(gradientOpacity * 0.5 * 2.55).toString(16).padStart(2, '0')} 50%, transparent 100%)`
                  : `linear-gradient(to top, ${gradientColor}${Math.round(gradientOpacity * 2.55).toString(16).padStart(2, '0')} 0%, ${gradientColor}${Math.round(gradientOpacity * 0.5 * 2.55).toString(16).padStart(2, '0')} 50%, transparent 100%)`,
            }}
          />
        )}

        {/* Design Elements — two layers by depth (utils/designElementLayers.ts):
            BEHIND sits above the gradient and border, under the set elements (z-[5]);
            IN FRONT is the top-most layer (z-20), today's look for every placement
            made before `depth` existed. Each layer is pointer-events:none so
            transparent areas click through; each PlaceableDesignElement re-enables
            pointer events on itself. A behind element's selection chrome is portaled
            into the chrome host (z-30) so its handles and pill are never under a title. */}
        {designLayers.back.length > 0 && (
          <div data-design-layer="back" className="absolute inset-0 z-[5] overflow-hidden rounded-lg pointer-events-none">
            {designLayers.back.map(renderDesignElement)}
          </div>
        )}

        {/* Overlay elements */}
        <div className="absolute inset-0 z-10">
          {/* ONE column on the safe rectangle: top stack, flexible middle, bottom stack (nothing overlaps) */}
          <div data-overlay-column="" style={{ ...stackColumnStyle(anchorZone), pointerEvents: 'none' }}>
            {renderRow('top')}
            {renderRow('middle')}
            {renderRow('bottom')}
          </div>
        </div>

        {/* Safe zone: the keep-out bands of this canvas (view for everyone; SA / CS drag the edges). */}
        <SafeZoneLayer
          insets={safeZone}
          visible={showSafeZones}
          editable={controlsEnabled && isSuperAdmin && !!safeZoneChannel}
          label={safeZoneLabel}
          publishSize={BUCKET_DIMENSIONS[safeBucket]}
          onSave={(insets) => setUniversalSafeZones({ [safeZoneChannel as string]: { [safeZonePlacement ?? 'default']: { ...insets, bucket: safeBucket } } })}
          onEditingChange={(editing) => { if (editing) clearSelection(); }}
        />

        {designLayers.front.length > 0 && (
          <div data-design-layer="front" className="absolute inset-0 z-20 overflow-hidden rounded-lg pointer-events-none">
            {designLayers.front.map(renderDesignElement)}
          </div>
        )}
        {/* Chrome host: the selection chrome of a behind design element (see above). */}
        <div ref={setDesignChromeHost} data-design-chrome-host="" className="absolute inset-0 z-30 overflow-hidden rounded-lg pointer-events-none" />

        {/* Design Elements picker — full-overlay popup */}
        {showDesignElementsPicker && (
          <DesignElementsPicker
            brandId={previewBrandId}
            isSuperAdmin={isSuperAdmin}
            canEditBrand={canEditPreviewBrand}
            currentPlacementCount={designElements?.length ?? 0}
            onSelect={(item) => {
              const arr = designElements || [];
              if (arr.length >= MAX_DESIGN_ELEMENTS_PER_OVERLAY) return;
              const instanceId =
                typeof crypto !== 'undefined' && (crypto as any).randomUUID
                  ? (crypto as any).randomUUID()
                  : `de-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
              const placement: PlacedDesignElement = {
                instanceId,
                elementId: item.id,
                url: item.s3_url,
                xAnchor: 'center',
                yAnchor: 'middle',
                xPct: 0,
                yPct: 0,
                widthPct: 30,
                rotation: 0,
                aspectRatio: item.aspect_ratio || 1,
                depth: 'back', // new placements start behind the text; the pill brings them in front
              };
              onConfigChange({ designElements: [...arr, placement] });
              selectElement({ kind: 'design', id: instanceId });
              setShowDesignElementsPicker(false);
            }}
            onClose={() => setShowDesignElementsPicker(false)}
          />
        )}

        {/* Controls panel - bottom right - matching TendV2ContentCard.
            Element-visibility toggles stack UPWARD from the settings puck;
            the panel-bearing controls (design/background/border) open
            left — the old single row overflowed the preview edge.
            Gone while an element is selected (element mode). */}
        {controlsEnabled && !elementMode && (
          <div className="absolute bottom-2 right-2 z-30 flex flex-col items-end gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none [&_button]:pointer-events-auto [&_input]:pointer-events-auto">
            {/* Element visibility toggles - vertical, above the puck */}
            {showControls && (
              <div className="flex flex-col items-center gap-1.5" data-testid="overlay-toggle-stack">
                {/* Category toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); onConfigChange({ showOverlayCategory: !showOverlayCategory }); }}
                  className={`p-1.5 rounded-full transition-all duration-200 text-white
                    ${showOverlayCategory ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                  title={showOverlayCategory ? "Hide category" : "Show category"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 7.994V3h2.01" />
                  </svg>
                </button>

                {/* Title toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); onConfigChange({ showOverlayTitle: !showOverlayTitle }); }}
                  className={`p-1.5 rounded-full transition-all duration-200 text-white
                    ${showOverlayTitle ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                  title={showOverlayTitle ? "Hide title" : "Show title"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M12 6v13" />
                  </svg>
                </button>

                {/* Subheader toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); onConfigChange({ showOverlaySubheader: !showOverlaySubheader }); }}
                  className={`p-1.5 rounded-full transition-all duration-200 text-white
                    ${showOverlaySubheader ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                  title={showOverlaySubheader ? "Hide subheader" : "Show subheader"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10" />
                  </svg>
                </button>

                {/* Body toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); onConfigChange({ showOverlayBody: !showOverlayBody }); }}
                  className={`p-1.5 rounded-full transition-all duration-200 text-white
                    ${showOverlayBody ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                  title={showOverlayBody ? "Hide body" : "Show body"}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h12" />
                  </svg>
                </button>

                {/* Logo toggle - shows the mark the template will actually
                    render (template-carried logo → brand default) so it's
                    clear WHICH logo the toggle controls */}
                <button
                  onClick={(e) => { e.stopPropagation(); onConfigChange({ showOverlayLogo: !showOverlayLogo }); }}
                  className={`p-1.5 rounded-full transition-all duration-200 text-white
                    ${showOverlayLogo ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                  title={showOverlayLogo ? "Hide logo" : "Show logo"}
                >
                  {effectiveLogo?.url ? (
                    <img
                      src={effectiveLogo.url}
                      alt=""
                      draggable={false}
                      className={`w-4 h-4 object-contain ${showOverlayLogo ? '' : 'opacity-50 grayscale'}`}
                    />
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth={2} />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 12h9v9h-9z" />
                    </svg>
                  )}
                </button>
              </div>
            )}

            {/* Toggle buttons row */}
            <div className="flex items-end gap-1.5">
              {/* Panel-bearing controls - open left of the puck */}
              {showControls && (
                <div className="flex items-center gap-1.5" data-testid="overlay-panel-row">
                  {/* Design Elements - opens picker over canvas */}
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowDesignElementsPicker((v) => !v); }}
                    className={`p-1.5 rounded-full transition-all duration-200 text-white
                      ${showDesignElementsPicker ? 'bg-blue-600 ring-2 ring-blue-400' : ((designElements?.length ?? 0) > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600')}`}
                    title="Design elements"
                  >
                    {/* Shapes glyph — "elements you place on a canvas";
                        the old arrows-out icon read as expand/fullscreen */}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="11" height="11" rx="1.5" strokeWidth={2} />
                      <circle cx="16" cy="16" r="5" strokeWidth={2} />
                    </svg>
                  </button>

                  {/* Safe zone guideline (per editor, off by default; off again when the canvas loses selection) */}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleSafeZones(); }}
                    className={`p-1.5 rounded-full transition-all duration-200 text-white
                      ${showSafeZones ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                    title={showSafeZones ? 'Hide safe zone' : 'Show safe zone'}
                    data-testid="safe-zone-toggle"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
                      <rect x="7" y="8" width="10" height="8" rx="1" strokeWidth={2} strokeDasharray="2 2" />
                    </svg>
                  </button>

                  {/* Gradient - expandable controls */}
                  <button
                    ref={gradientBtnRef}
                    onClick={(e) => { e.stopPropagation(); setShowGradientPanel(!showGradientPanel); }}
                    className={`p-1.5 rounded-full transition-all duration-200 text-white
                      ${showGradientPanel ? 'bg-blue-600 ring-2 ring-blue-400' : showOverlayGradient ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                    title="Background settings"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 9h2v2h-2zm-2 2h2v2H9zm4 0h2v2h-2zm2-2h2v2h-2zM7 9h2v2H7zm12-6H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 18H7v-2h2v2zm4 0h-2v-2h2v2zm4 0h-2v-2h2v2zm2-7h-2v2h2v2h-2v-2h-2v2h-2v-2h-2v2H5v-2h2v-2H5V5h14v6z" />
                    </svg>
                  </button>
                  {/* Gradient controls panel - portaled so the preview's
                      overflow-hidden (and the modal's) can't clip it */}
                  {showGradientPanel && (
                    <AnchoredStripPopover
                      anchorRef={gradientBtnRef}
                      onClose={() => setShowGradientPanel(false)}
                      suspendOutsideClose={isColorPickerOpen}
                    >
                      {/* Hide/Show toggle */}
                      <button
                        type="button"
                        onClick={() => onConfigChange({ showOverlayGradient: !showOverlayGradient })}
                        className="p-0.5 text-white/70 hover:text-white transition-colors"
                        title={showOverlayGradient ? "Hide gradient" : "Show gradient"}
                      >
                        {showOverlayGradient ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        )}
                      </button>
                      {/* Direction buttons */}
                      {(['up', 'down', 'full'] as const).map((dir) => (
                        <button
                          key={dir}
                          onClick={() => onConfigChange({ gradientDirection: dir })}
                          className={`p-0.5 text-sm transition-colors ${gradientDirection === dir ? 'text-white' : 'text-white/50 hover:text-white/80'}`}
                          title={dir === 'up' ? 'Bottom to top' : dir === 'down' ? 'Top to bottom' : 'Full overlay'}
                        >
                          {dir === 'up' ? '↑' : dir === 'down' ? '↓' : '▣'}
                        </button>
                      ))}
                      {/* Color swatch */}
                      <ColorPickerPopover
                        color={gradientColor}
                        onChange={(color) => onConfigChange({ gradientColor: color })}
                        triggerSize="md"
                        position="above"
                        title="Gradient color"
                        onOpenChange={setIsColorPickerOpen}
                      />
                      {/* Opacity slider */}
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={gradientOpacity}
                        onChange={(e) => onConfigChange({ gradientOpacity: parseInt(e.target.value) })}
                        className="w-16 h-1.5 accent-blue-500 cursor-pointer"
                        title="Opacity"
                      />
                    </AnchoredStripPopover>
                  )}

                  {/* Border - expandable controls */}
                  <button
                    ref={borderBtnRef}
                    onClick={(e) => { e.stopPropagation(); setShowBorderPanel(!showBorderPanel); }}
                    className={`p-1.5 rounded-full transition-all duration-200 text-white
                      ${showBorderPanel ? 'bg-blue-600 ring-2 ring-blue-400' : borderEnabled ? 'bg-blue-600 hover:bg-blue-700' : 'bg-black/60 hover:bg-green-600'}`}
                    title="Border settings"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth={2} />
                    </svg>
                  </button>
                  {/* Border controls panel - portaled, same reason */}
                  {showBorderPanel && (
                    <AnchoredStripPopover
                      anchorRef={borderBtnRef}
                      onClose={() => setShowBorderPanel(false)}
                      suspendOutsideClose={isColorPickerOpen}
                    >
                      {/* Hide/Show toggle */}
                      <button
                        type="button"
                        onClick={() => onConfigChange({ borderEnabled: !borderEnabled })}
                        className="p-0.5 text-white/70 hover:text-white transition-colors"
                        title={borderEnabled ? "Hide border" : "Show border"}
                      >
                        {borderEnabled ? (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          </svg>
                        )}
                      </button>
                      {/* Color swatch */}
                      <ColorPickerPopover
                        color={borderColor}
                        onChange={(color) => onConfigChange({ borderColor: color })}
                        triggerSize="md"
                        position="above"
                        title="Border color"
                        onOpenChange={setIsColorPickerOpen}
                      />
                      {/* Thickness slider */}
                      <input
                        type="range"
                        min="1"
                        max="12"
                        value={borderThickness}
                        onChange={(e) => onConfigChange({ borderThickness: parseInt(e.target.value) })}
                        className="w-16 h-1.5 accent-blue-500 cursor-pointer"
                        title={`Thickness: ${borderThickness}px`}
                      />
                    </AnchoredStripPopover>
                  )}
                </div>
              )}

              {/* Settings button - opens/closes toggle panel */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowControls(!showControls);
                  if (showControls) {
                    setShowGradientPanel(false);
                    setShowBorderPanel(false);
                  }
                }}
                className={`p-1.5 rounded-full text-white transition-colors ${showControls ? 'bg-blue-600' : 'bg-black/60 hover:bg-white/30'}`}
                title="Overlay settings"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverlayPreview;
